import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import PaymentIcon from "@mui/icons-material/Payment";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SchoolIcon from "@mui/icons-material/School";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import usePermissions from "../hooks/usePermissions";
import {
  setSideNavOpen,
  toggleSideNavCollapsed,
} from "../store/slices/uiSlice";

const SideNavigation = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const sideNavOpen = useSelector((state) => state.ui.sideNavOpen);
  const sideNavCollapsed = useSelector((state) => state.ui.sideNavCollapsed);
  const user = useSelector((state) => state.auth.user);
  const effectiveSideNavCollapsed = isMobile ? false : sideNavCollapsed;
  const { hasPermission, hasRole } = usePermissions();

  const [expandedGroup, setExpandedGroup] = useState(
    hasRole("STUDENT") ? "My Portfolio" : "Academic Operations",
  );

  const handleToggleGroup = (group) => {
    setExpandedGroup((prev) => (prev === group ? null : group));
  };

  const navHeaderMinHeight = 64;
  const navItemMinHeight = 40;
  const navListPaddingY = 0.75;
  const navLogoSize = 45;
  const navTitleSize = 22;
  const navTooltipDelay = 150;
  const navLabelFontSize = isMobile ? "0.9rem" : "1rem";

  // Call the hook inside the component
  const isNonStudentUser = !hasRole("STUDENT");
  const isStudentUser = hasRole("STUDENT");
  const studentId = user?.entityId || user?.id;
  const student_access =
    isNonStudentUser &&
    (hasPermission("STUDENTS", "VIEW") || hasRole(["SUPER_ADMIN"]));
  const exams_access =
    isNonStudentUser &&
    (hasPermission("EXAMS", "VIEW") ||
      hasPermission("GRADING", "VIEW") ||
      hasRole(["SUPER_ADMIN"]));

  // Define navItems with permission checks
  const navItems = useMemo(
    () => [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: <DashboardIcon />,
        group: "Academic Operations",
        visible: hasPermission("DASHBOARD", "VIEW") || hasRole(["SUPER_ADMIN"]),
      },
      {
        label: "My Profile",
        path: isStudentUser && studentId ? `/student/${studentId}/view` : "#",
        icon: <AccountCircleIcon />,
        group: "My Portfolio",
        visible: isStudentUser,
      },
      {
        label: "My Fees",
        path:
          isStudentUser && studentId ? `/student/${studentId}/fees/view` : "#",
        icon: <PaymentIcon />,
        group: "My Portfolio",
        visible: isStudentUser,
      },
      {
        label: "My Attendance",
        path:
          isStudentUser && studentId ? `/student/${studentId}/attendance` : "#",
        icon: <CalendarMonthIcon />,
        group: "My Portfolio",
        visible: isStudentUser,
      },
      {
        label: "My Marks",
        path: isStudentUser && studentId ? `/student/${studentId}/marks` : "#",
        icon: <AssignmentIcon />,
        group: "My Portfolio",
        visible: isStudentUser,
      },
      {
        label: "My Promotion",
        path:
          isStudentUser && studentId
            ? `/student/promotion/history/${studentId}`
            : "#",
        icon: <TrendingUpIcon />,
        group: "My Portfolio",
        visible: isStudentUser,
      },
      {
        label: "My Timetable",
        path: "/my-timetable",
        icon: <ViewWeekIcon />,
        group: hasRole("TEACHER") ? "Self Service" : "My Portfolio",
        visible: hasRole(["STUDENT", "TEACHER"]),
      },
      {
        label: "EduBot AI",
        path: "/edubot",
        icon: <SmartToyIcon />,
        group: isStudentUser ? "My Portfolio" : "Self Service",
        visible: process.env.REACT_APP_ENABLE_EDUBOT === "true",
      },
      {
        label: "Students",
        path: "/student",
        icon: <SchoolIcon />,
        group: "Academic Operations",
        visible: student_access,
      },
      {
        label: "Teachers",
        path: "/teacher",
        icon: <PersonIcon />,
        group: "Administration",
        visible:
          !hasRole("STAFF") &&
          (hasPermission("EMPLOYEES", "VIEW") || hasRole(["SUPER_ADMIN"])),
      },
      {
        label: "Employees",
        path: "/employee",
        icon: <PeopleIcon />,
        group: "Administration",
        visible: hasPermission("EMPLOYEES", "VIEW") || hasRole(["SUPER_ADMIN"]),
      },
      {
        label: "Attendance",
        path: "/attendance/mark",
        icon: <CalendarMonthIcon />,
        group: "Academic Operations",
        visible:
          hasPermission("ATTENDANCE", "MARK") || hasRole(["SUPER_ADMIN"]),
      },
      {
        label: "Leave Request",
        path: "/attendance/staff-leave",
        icon: <EventAvailableIcon />,
        group: "Self Service",
        visible:
          isNonStudentUser &&
          (hasPermission("ATTENDANCE", "MARK") || hasRole(["SUPER_ADMIN"])),
      },
      {
        label: "My HR",
        path: "/my-hr",
        icon: <ReceiptLongIcon />,
        group: "Self Service",
        visible: hasRole(["TEACHER", "STAFF", "ACCOUNTANT"]),
      },
      {
        label: "Fees Management",
        path: "/fees",
        icon: <PaymentIcon />,
        group: "Administration",
        visible:
          (hasRole(["SUPER_ADMIN"]) || hasPermission("FEES", "VIEW")) &&
          student_access,
      },
      {
        label: "Financial",
        path: "/financial",
        icon: <AccountBalanceIcon />,
        group: "Administration",
        visible:
          hasRole(["ADMIN", "SUPER_ADMIN"]) ||
          hasPermission("FINANCIAL", "VIEW"),
      },
      {
        label: "Exams & Grading",
        path: "/exams",
        icon: <FactCheckIcon />,
        group: "Academic Operations",
        visible: exams_access,
      },
      {
        label: "Admissions",
        path: "/admissions",
        icon: <GroupAddIcon />,
        group: "Academic Operations",
        visible: hasPermission("ADMISSIONS", "VIEW") || hasRole(["SUPER_ADMIN"]),
      },
      {
        label: "School Calendar",
        path: "/events-calendar",
        icon: <EventAvailableIcon />,
        group: "Academic Operations",
        visible: hasPermission("EVENTS", "VIEW") || hasRole(["SUPER_ADMIN"]),
      },
      {
        label: "Admin",
        path: "/admin",
        icon: <AdminPanelSettingsIcon />,
        group: "Administration",
        visible: hasRole(["ADMIN", "SUPER_ADMIN"]),
      },
    ],
    [
      exams_access,
      hasPermission,
      hasRole,
      isNonStudentUser,
      isStudentUser,
      studentId,
      student_access,
    ],
  );

  // Filter visible items
  const visibleNavItems = useMemo(
    () => navItems.filter((item) => item.visible),
    [navItems],
  );
  const groupOrder = [
    "My Portfolio",
    "Academic Operations",
    "Self Service",
    "Administration",
  ];
  const groupedVisibleNavItems = useMemo(
    () =>
      groupOrder
        .map((group) => ({
          group,
          items: visibleNavItems.filter((item) => item.group === group),
        }))
        .filter((section) => section.items.length > 0),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleNavItems],
  );

  const drawerWidth = effectiveSideNavCollapsed ? 68 : 248;
  const responsiveDrawerWidth = isMobile ? 260 : drawerWidth;

  const DrawerList = (
    <Box
      sx={{
        width: responsiveDrawerWidth,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: effectiveSideNavCollapsed
              ? "center"
              : "space-between",
            px: 2,
            pt: 1,
            minHeight: navHeaderMinHeight,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ minHeight: 50 }}
          >
            <img
              src={process.env.PUBLIC_URL + "/ischool-logo.png"}
              alt="iSchool Logo"
              style={{
                height: navLogoSize,
                width: navLogoSize,
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
            {!effectiveSideNavCollapsed && (
              <Typography
                variant="h6"
                noWrap
                sx={{
                  letterSpacing: 1,
                  ml: 0.5,
                  color: "primary.main",
                  fontSize: navTitleSize,
                  lineHeight: 1.2,
                  display: "flex",
                  alignItems: "center",
                  minHeight: navLogoSize,
                }}
              >
                iSchool
              </Typography>
            )}
          </Box>
          {!isMobile && (
            <IconButton
              onClick={() => dispatch(toggleSideNavCollapsed())}
              size="small"
              sx={{ paddingLeft: effectiveSideNavCollapsed ? 0 : "5px" }}
              aria-label={
                effectiveSideNavCollapsed
                  ? "Expand sidebar"
                  : "Collapse sidebar"
              }
            >
              {effectiveSideNavCollapsed ? (
                <ChevronRightIcon />
              ) : (
                <ChevronLeftIcon />
              )}
            </IconButton>
          )}
          {isMobile && (
            <IconButton
              onClick={() => dispatch(setSideNavOpen(false))}
              size="small"
              aria-label="Close sidebar"
            >
              <MenuOpenIcon />
            </IconButton>
          )}
        </Box>
        <Divider />
        <List
          disablePadding
          sx={{
            px: effectiveSideNavCollapsed ? 0.75 : 1,
            py: navListPaddingY,
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            minHeight: 0,
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {groupedVisibleNavItems.map((section, sectionIdx) => (
            <Box
              key={section.group}
              sx={{
                mb: sectionIdx !== groupedVisibleNavItems.length - 1 ? 1 : 0,
              }}
            >
              {!effectiveSideNavCollapsed && (
                <ListItemButton
                  onClick={() => handleToggleGroup(section.group)}
                  sx={{
                    py: 0.5,
                    px: 2,
                    borderRadius: 1.5,
                    "&:hover": { bgcolor: "transparent" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      lineHeight: "2.5rem",
                    }}
                  >
                    {section.group}
                  </Typography>
                  {expandedGroup === section.group ? (
                    <ExpandLess
                      sx={{ fontSize: "1.2rem", color: "text.secondary" }}
                    />
                  ) : (
                    <ExpandMore
                      sx={{ fontSize: "1.2rem", color: "text.secondary" }}
                    />
                  )}
                </ListItemButton>
              )}
              {effectiveSideNavCollapsed && <Divider sx={{ my: 1, mx: 1 }} />}
              <Collapse
                in={
                  effectiveSideNavCollapsed
                    ? true
                    : expandedGroup === section.group
                }
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {section.items.map((item) => {
                    const isActive =
                      location.pathname === item.path ||
                      location.pathname.startsWith(item.path + "/");

                    return (
                      <ListItem
                        key={item.label}
                        disablePadding
                        sx={{ mb: 0.1 }}
                      >
                        <Tooltip
                          title={effectiveSideNavCollapsed ? item.label : ""}
                          placement="right"
                          arrow
                          enterDelay={navTooltipDelay}
                          enterNextDelay={navTooltipDelay}
                          disableInteractive
                        >
                          <ListItemButton
                            onClick={() => {
                              navigate(item.path);
                              if (isMobile) dispatch(setSideNavOpen(false));
                            }}
                            selected={isActive}
                            sx={{
                              minHeight: navItemMinHeight,
                              px: effectiveSideNavCollapsed ? 0.75 : 1.5,
                              justifyContent: effectiveSideNavCollapsed
                                ? "center"
                                : "flex-start",
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: isActive
                                ? "primary.main"
                                : "transparent",
                              backgroundColor: isActive
                                ? "action.selected"
                                : "transparent",
                              "&:hover": {
                                backgroundColor: isActive
                                  ? "action.selected"
                                  : "action.hover",
                              },
                              "&.Mui-selected": {
                                backgroundColor: "action.selected",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.02)",
                                "&:hover": {
                                  backgroundColor: "action.selected",
                                },
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isActive
                                  ? "primary.main"
                                  : "text.primary",
                                minWidth: effectiveSideNavCollapsed ? 0 : 38,
                                mr: effectiveSideNavCollapsed ? 0 : 1,
                                justifyContent: "center",
                                "& svg": {
                                  fontSize: 24,
                                },
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            {!effectiveSideNavCollapsed && (
                              <ListItemText
                                primary={item.label}
                                slotProps={{
                                  primary: {
                                    sx: {
                                      fontWeight: isActive ? 700 : 500,
                                      fontSize: navLabelFontSize,
                                    },
                                  },
                                }}
                              />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
        <Divider sx={{ flexShrink: 0 }} />
        <Box
          sx={{
            px: effectiveSideNavCollapsed ? 0.75 : 1,
            py: 0.75,
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            flexShrink: 0,
            backgroundColor: "background.paper",
            boxShadow: "0 -1px 0 rgba(0, 0, 0, 0.08)",
          }}
        >
          <ListItem disablePadding>
            <Tooltip
              title={effectiveSideNavCollapsed ? "Logout" : ""}
              placement="right"
              arrow
              enterDelay={navTooltipDelay}
              enterNextDelay={navTooltipDelay}
              disableInteractive
            >
              <ListItemButton
                onClick={onLogout}
                sx={{
                  minHeight: navItemMinHeight,
                  px: effectiveSideNavCollapsed ? 0.75 : 1.5,
                  justifyContent: effectiveSideNavCollapsed
                    ? "center"
                    : "flex-start",
                  borderRadius: 1.5,
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: (muiTheme) =>
                      muiTheme.palette.mode === "dark"
                        ? "rgba(211, 47, 47, 0.2)"
                        : "rgba(211, 47, 47, 0.1)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "error.main",
                    minWidth: effectiveSideNavCollapsed ? 0 : 38,
                    mr: effectiveSideNavCollapsed ? 0 : 1,
                    justifyContent: "center",
                    "& svg": {
                      fontSize: 24,
                    },
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                {!effectiveSideNavCollapsed && (
                  <ListItemText
                    primary="Logout"
                    slotProps={{
                      primary: {
                        sx: {
                          fontWeight: 700,
                          fontSize: navLabelFontSize,
                          color: "error.main",
                        },
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </Box>
      </Box>
    </Box>
  );

  return (
    <div className="side-nav">
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        anchor="left"
        open={isMobile ? sideNavOpen : true}
        onClose={() => dispatch(setSideNavOpen(false))}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: responsiveDrawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: responsiveDrawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            overflowY: "hidden",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        {DrawerList}
      </Drawer>
    </div>
  );
};

SideNavigation.propTypes = {
  onLogout: PropTypes.func.isRequired,
};

export default SideNavigation;
